/**
 * The few values every panel in `ui/` agrees on. No CSS pipeline — inline
 * styles — so sharing a look means sharing a constant. Deliberately tiny.
 */

/** The one font. Monospace because every readout here is a number in a column. */
export const FONT =
  "12px/1.35 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** Panel and pill fill. Dark and translucent: the raid stays visible behind it. */
export const PANEL_BG = "rgba(16, 18, 21, 0.93)";

/** Popup fill. Menus must fully occlude the item labels directly beneath them. */
export const MENU_BG = "rgb(16, 18, 21)";

/** The hairline every panel, pill and grid is outlined with. */
export const PANEL_LINE = "rgba(214, 205, 194, 0.20)";

/** Body text. Slightly warm white — the HUD's own colour. */
export const TEXT = "#e3eaf1";

/** Labels, capacities, hints: present but never competing with the numbers. */
export const TEXT_DIM = "rgba(227, 234, 241, 0.55)";

// ---------------------------------------------------------------------------
// The menu's design tokens. The raid HUD keeps the monospace readouts above;
// everything between raids (`ui/meta/`, `settings.tsx`, `auth.tsx`) reads
// from here so the eight tabs, the modals and the sign-in form are one look.
// ---------------------------------------------------------------------------

/** Sans family for menu prose, labels and headings. System-native, no webfont. */
export const UI_FAMILY =
  '"Segoe UI", Inter, system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';

/** Mono family for numbers, prices, counts and anything in a column. */
export const MONO_FAMILY = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/** The menu's body font. */
export const UI_FONT = `13px/1.45 ${UI_FAMILY}`;

/** Brand warm gold: the deploy button, selected tabs, prices, level badges. */
export const ACCENT = "#d6b06c";
/** Lit gold, for hover and the brightest text on a dark card. */
export const ACCENT_HI = "#ecc98a";
/** Ink on a gold button. */
export const ACCENT_INK = "#1b1a17";
/** Translucent gold wash behind a selected row or an active chip. */
export const ACCENT_WASH = "rgba(214, 176, 108, 0.14)";

export const GOOD = "#8fd39a";
export const BAD = "#e08076";
export const WARN = "#e0c184";

/** A card on the backdrop. */
export const SURFACE = "rgba(19, 22, 26, 0.86)";
/** A raised control or nested panel inside a card. */
export const SURFACE_2 = "rgba(28, 32, 38, 0.92)";
/** The 1 px card edge. Softer than `PANEL_LINE`; cards read by tone, not outline. */
export const LINE = "rgba(214, 205, 194, 0.12)";
export const LINE_STRONG = "rgba(214, 205, 194, 0.24)";

export const RADIUS = 10;
export const RADIUS_SM = 6;

/** Card lift: a hairline of light on top, soft shadow below. */
export const SHADOW_CARD =
  "inset 0 1px 0 rgba(255, 255, 255, 0.035), 0 10px 30px rgba(0, 0, 0, 0.35)";
/** Modal lift. */
export const SHADOW_MODAL = "0 24px 80px rgba(0, 0, 0, 0.65)";
/** Keyboard focus ring, shared by every control. */
export const FOCUS_RING = "0 0 0 2px rgba(236, 201, 138, 0.85)";
